import { serverNow } from "@storyboard/web-client";
import { nameForMembership } from "@/hooks/dispatch/useDispatchThread";
import type { TmsEventRow } from "@/store/types";
import {
  type AssignmentIntervalDebugRow,
  assignmentIntervalDebugRows,
  injectSyntheticVehicleEvent,
  removeSyntheticEvents,
  type SyntheticVehicleEventInput,
} from "./assignmentDebug";
import { flightRecorder, type Summary, type Tick } from "./FlightRecorder";
import { formatReport } from "./formatReport";

/** The `window.__sbDebug` surface: what an agent driving a browser, or a person in DevTools, calls. */
export type DebugBridge = {
  /** Resets, waits `ms`, and resolves the summary of exactly that window. */
  record(ms: number): Promise<Summary>;
  /** Aggregates the last `windowMs` (default 60s). */
  summary(windowMs?: number): Summary;
  /** The summary as pasteable Markdown. */
  report(windowMs?: number): string;
  /** The most recent ticks, newest first. */
  ticks(limit?: number): Tick[];
  reset(): void;
  /** The driver-on-vehicle assignment records the store holds and attribution reads, for `console.table`. */
  assignmentIntervals(): AssignmentIntervalDebugRow[];
  /**
   * Lands a synthetic arrival on a unit at an instant with no driver, in the local store only, and returns it with the
   * drivers it was attributed to; the URL's workspace by default.
   */
  injectVehicleEvent(
    input: Omit<SyntheticVehicleEventInput, "workspaceId"> & { workspaceId?: string },
  ): Promise<TmsEventRow>;
  /** Removes every event `injectVehicleEvent` landed, returning how many. */
  removeSyntheticEvents(): number;
};

declare global {
  interface Window {
    __sbDebug?: DebugBridge;
  }
}

/** Installs `window.__sbDebug`; debug mode only. */
export function installDebugBridge(): void {
  window.__sbDebug = {
    record: (ms) => flightRecorder.record(ms),
    summary: (windowMs) => flightRecorder.summary(windowMs),
    report: (windowMs) => formatReport(flightRecorder.summary(windowMs)),
    ticks: (limit) => flightRecorder.recentTicks(limit),
    reset: () => flightRecorder.reset(),
    assignmentIntervals: () => assignmentIntervalDebugRows(serverNow().toISOString(), nameForMembership),
    injectVehicleEvent: (input) =>
      injectSyntheticVehicleEvent(
        { ...input, workspaceId: input.workspaceId ?? workspaceIdFromUrl() },
        serverNow().toISOString(),
      ),
    removeSyntheticEvents: () => removeSyntheticEvents(),
  };
}

/** The workspace the page shows, read from its `/workspaces/<id>` path at call time. */
function workspaceIdFromUrl(): string {
  const workspaceId = /\/workspaces\/(?<workspaceId>[^/]+)/.exec(window.location.pathname)?.groups?.workspaceId;
  if (workspaceId === undefined) throw new Error("No workspace in the URL; pass workspaceId");
  return workspaceId;
}
